import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro } from "../../hooks/useAdminApi";

/**
 * SEGURANÇA — ativar o segundo fator (2FA) da própria conta.
 *
 * Por que isto existe: o painel passou a criar produto e mexer em preço na loja. A senha
 * sozinha deixou de ser proteção suficiente para operação que envolve dinheiro.
 *
 * ⚠️ Os CÓDIGOS DE BACKUP aparecem UMA ÚNICA VEZ, na ativação. Depois disso o servidor só
 * guarda o hash — não tem como mostrar de novo. Por isso a tela obriga a pessoa a marcar
 * "guardei" antes de sair da etapa: sem eles, perder o celular é perder o painel.
 */
export default function Seguranca() {
  const { tema, setTema } = useOutletContext();
  const { erro, rodar, limparErro } = useAdminState();

  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // { uri, segredo }
  const [codigo, setCodigo] = useState("");
  const [backup, setBackup] = useState(null); // códigos em claro (só agora)
  const [guardei, setGuardei] = useState(false);

  const carregar = useCallback(
    () => rodar(async () => setStatus(await apiGet(API_ROUTES.TWOFA.status()))),
    [rodar]
  );
  useEffect(() => {
    carregar();
  }, [carregar]);

  const iniciar = () =>
    rodar(async () => setSetup(await apiSend(API_ROUTES.TWOFA.setup(), "POST", {})));

  const ativar = () =>
    rodar(async () => {
      const r = await apiSend(API_ROUTES.TWOFA.enable(), "POST", { code: codigo });
      setBackup(r.codigosDeBackup);
      setSetup(null);
      setCodigo("");
      await carregar();
    });

  const desativar = () =>
    rodar(async () => {
      const c = window.prompt(
        "Para desativar o segundo fator, digite um código do app (ou um código de backup):"
      );
      if (!c) return;
      await apiSend(API_ROUTES.TWOFA.disable(), "POST", { code: c });
      setBackup(null);
      await carregar();
    });

  return (
    <>
      <AdminHeader titulo="Segurança" tema={tema} setTema={setTema} />
      <Erro erro={erro} onFechar={limparErro} />

      {/* ---- códigos de backup: momento único ---- */}
      {backup && (
        <section style={{ ...s.bloco, borderColor: "var(--green)" }}>
          <h3 style={s.h3}>✅ Segundo fator ativado — guarde estes códigos AGORA</h3>
          <p style={s.p}>
            Eles são a <strong>única forma de entrar se você perder o celular</strong>. Cada um
            funciona <strong>uma vez só</strong>. Esta tela <strong>não será mostrada de novo</strong> —
            o servidor guarda apenas o hash deles.
          </p>
          <div style={s.codigos}>
            {backup.map((c) => (
              <code key={c} style={s.codigo}>{c}</code>
            ))}
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14 }}>
            <input type="checkbox" checked={guardei} onChange={(e) => setGuardei(e.target.checked)} />
            <span style={{ fontSize: 13 }}>Guardei os códigos num lugar seguro</span>
          </label>
          <button
            style={{ ...s.btn, marginTop: 12, opacity: guardei ? 1 : 0.4 }}
            disabled={!guardei}
            onClick={() => setBackup(null)}
          >
            Concluir
          </button>
        </section>
      )}

      {/* ---- estado atual ---- */}
      {!backup && status && (
        <section style={s.bloco}>
          <h3 style={s.h3}>Verificação em duas etapas</h3>
          {status.ativo ? (
            <>
              <p style={s.p}>
                <span style={{ color: "var(--green)", fontWeight: 700 }}>● Ativa.</span> Ao entrar,
                além da senha será pedido um código do seu app autenticador.
              </p>
              <p style={s.p}>
                Códigos de backup restantes: <strong>{status.codigosDeBackupRestantes}</strong>
                {status.codigosDeBackupRestantes <= 2 && (
                  <span style={{ color: "var(--orange)" }}>
                    {" "}— estão acabando. Desative e ative de novo para gerar outros.
                  </span>
                )}
              </p>
              <button style={s.btnOff} onClick={desativar}>Desativar</button>
            </>
          ) : (
            <>
              <p style={s.p}>
                <span style={{ color: "var(--text3)", fontWeight: 700 }}>○ Desativada.</span>{" "}
                Hoje só a senha protege este painel — que cria produto e altera preço na loja.
              </p>
              {!setup && <button style={s.btn} onClick={iniciar}>Ativar segundo fator</button>}
            </>
          )}
        </section>
      )}

      {/* ---- enrolamento ---- */}
      {setup && (
        <section style={s.bloco}>
          <h3 style={s.h3}>1. Leia o QR no seu app autenticador</h3>
          <p style={s.p}>
            Use Google Authenticator, Authy, 1Password ou similar. Se não conseguir ler o QR,
            digite o código manualmente.
          </p>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
            <div style={s.qr}>
              <QRCodeSVG value={setup.uri} size={190} level="M" bgColor="#ffffff" fgColor="#0e0e10" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 4 }}>
                Código manual:
              </div>
              <code style={s.segredo}>{setup.segredo}</code>
            </div>
          </div>

          <h3 style={{ ...s.h3, marginTop: 22 }}>2. Digite o código que aparecer</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              style={s.input}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.trim())}
              placeholder="000000"
              inputMode="numeric"
              maxLength={8}
            />
            <button style={s.btn} onClick={ativar} disabled={codigo.length < 6}>
              Confirmar e ativar
            </button>
            <button style={s.btnOff} onClick={() => setSetup(null)}>Cancelar</button>
          </div>
        </section>
      )}
    </>
  );
}

const s = {
  bloco: { background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: 18, marginBottom: 18 },
  h3: { margin: "0 0 10px", fontSize: 16 },
  p: { fontSize: 14, color: "var(--text2, #bbb)", lineHeight: 1.6, margin: "0 0 12px", maxWidth: 640 },
  qr: { background: "#fff", padding: 14, borderRadius: 12, lineHeight: 0 },
  segredo: { fontSize: 14, letterSpacing: 2, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", display: "inline-block", wordBreak: "break-all", maxWidth: 260 },
  input: { padding: "10px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg)", color: "var(--text)", fontSize: 20, letterSpacing: 6, textAlign: "center", width: 160 },
  btn: { background: "var(--red)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, cursor: "pointer" },
  btnOff: { background: "transparent", color: "var(--text3)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 18px", fontWeight: 700, cursor: "pointer" },
  codigos: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, maxWidth: 560 },
  codigo: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", textAlign: "center", fontSize: 15, letterSpacing: 2, fontWeight: 700 },
};
